import Model from '../Model'
import {debounce, isNull} from "lodash";
import {uuid} from "../../utils";
import moment from "moment";
import * as math from 'mathjs';
import sha256 from "fast-sha256";

export default class Estimate extends Model {
    async acquireEditLock(connectionId) {
        return await Http.put(route('api.v2.estimation.estimate.acquire-edit-lock', [this.id]), {connectionId})
    }

    watchTrigger = 0;

    async releaseEditLock() {
        return await Http.put(route('api.v2.estimation.estimate.release-edit-lock', [this.id]))
    }

    getDownloadUrl() {
        return route('api.v2.estimation.estimate.pdf', [this.packageReference, this.id])
    }
    getDownloadXLSXUrl() {
        return route('api.v2.estimation.estimate.xlsx', [this.packageReference, this.id])
    }

    colors(x) {
        const c = [
            '#FFFFFF',
            '#F0FFFF',
            '#F0F7FF',
            '#F0F7F7',
            '#F0FFF7',
            '#FFF0FF',
            '#F7F0FF',
            '#FFF0F7',
            '#F7F0F7',
            '#FFFFF0',
            '#F7FFF0',
            '#FFF7F0',
            '#F7F7F0',
            '#F0F0FF',
            '#F0F0F7',
            '#FFF0F0',
            '#F7F0F0',
            '#FFF0FF',
            '#F7F0FF',
            '#FFF0F7',
            '#FFFFF0',
            '#F7FFF0',
            '#FFF7F0',
            '#F7F7F0',
            '#F7FFFF',
            '#FFF7FF',
            '#FFFFF7',
            '#F7F7FF',
            '#FFF7F7',
        ]
        return c[x]
    }

    getPrintableUrl() {
        return route('api.v2.estimation.estimate.download', [this.packageReference, this.id])
    }

    static async list(packageReference = null) {
        const data = await Http.get(route('api.v2.estimation.estimate.list', packageReference))
        return this.make(data)
    }

    static itemAsKey(item, hashed = true) {
        const key = JSON.stringify({
            uom: item.uom,
            materials: item.materials.map((m) => {
                return {
                    component: m.component || '',
                    name: m.name, supplier: m.supplier, cost: m.cost, uom: m.uom, coverage: m.coverage, waste: m.waste
                };
            }),
            labour: item.labour.map((l) => {
                return {
                    component: l.component || '',
                    name: l.name, rate: l.rate
                };
            }),
            fixedCosts: item.fixedCosts.map((f) => {
                return {
                    component: f.component || '',
                    description: f.description, quantity: f.quantity, unit: f.unit, rate: f.rate, total: f.total
                };
            }),
        })
        if (hashed) {
            return sha256(new TextEncoder().encode(key)).reduce((acc, c) => acc + c.toString(16), '');
        }
        return key;
    }

    setColor(item, color) {
        this.colorMap[Estimate.itemAsKey(item)] = color
    }

    setShape(item, shape) {
        this.shapeMap[Estimate.itemAsKey(item)] = shape
    }

    setTitle(item, title) {
        this.titleMap[Estimate.itemAsKey(item)] = title
    }

    getTitle(item) {
        let itemKey = Estimate.itemAsKey(item);
        if (!this.titleMap[itemKey]) {
            // load first matching title of item
            this.titleMap[itemKey] = item.description
        }
        return this.titleMap[itemKey];
    }

    getShape(item) {
        let itemKey = Estimate.itemAsKey(item);
        const shapes = ['circle', 'square', 'triangle', 'star']
        if (!this.shapeMap[itemKey]) {
            this.shapeMap[itemKey] = shapes[Array.from(this.collectedItems.items.values()).findIndex((i) => i.item === item) % 4];
        }
        return this.shapeMap[itemKey];
    }

    getColor(item) {
        let itemKey = Estimate.itemAsKey(item);
        if (!this.colorMap[itemKey]) {
            let o = Estimate.itemAsKey(item, false) + Array.from(this.collectedItems.items.values()).findIndex((i) => i.item === item)
            let hashCode = function (str, multi) {
                let h = 0;
                for (let i = 0; i < str.length; i++) {
                    h = (str.charCodeAt(i) * multi) + ((h << 5) - h);
                }
                return h;
            }
            let defaultCode = `hsl(${hashCode(o, 17) % 360}, ` + (70 + (hashCode(o, 6) % 30)) + `%, ` + (60 - (hashCode(o, 3) % 20)) + `%)`;
            this.colorMap[itemKey] = defaultCode
        }
        return this.colorMap[itemKey];
    }

    static buildMapFromItem(item, collector) {
        let itemKey = Estimate.itemAsKey(item);
        if (!collector.items.has(itemKey)) {
            collector.items.set(itemKey, {
                item: item,
                items: [item],
                quantity: 0,
                hasBundle: !isNull(item.bundle),
            })
        } else {
            collector.items.get(itemKey).items.push(item)
        }
        if (item.bundle) {
            for (let i = 0; i < item.bundle.length; i++) {
                Estimate.buildMapFromItem(item.bundle[i], collector);
            }
        } else {
            collector.items.get(itemKey).quantity += 1;
            for (let i = 0; i < item.materials.length; i++) {
                let key = item.materials[i].name + '|' + item.materials[i].supplier + '|' + item.materials[i].cost;
                if (!collector.materials.has(key)) {
                    collector.materials.set(key, {
                        name: item.materials[i].name,
                        unitOfMeasure: item.materials[i].uom,
                        supplier: item.materials[i].supplier,
                        cost: item.materials[i].cost,
                        quantity: 0
                    })
                }
                if (item.quantity) {
                    collector.materials.get(key).quantity += item.materials[i].quantity;
                }
            }
            for (let i = 0; i < item.labour.length; i++) {
                let key = item.labour[i].name + '|' + item.labour[i].rate;
                if (!collector.labour.has(key)) {
                    collector.labour.set(key, {
                        name: item.labour[i].name,
                        rate: item.labour[i].rate,
                        quantity: 0
                    })
                }
                if (item.quantity) {
                    collector.labour.get(key).quantity += item.quantity;
                }
            }
            for (let i = 0; i < item.fixedCosts.length; i++) {
                let key = item.fixedCosts[i].name + '|' + item.fixedCosts[i].rate + +'|' + item.fixedCosts[i].uom;
                if (!collector.fixedCosts.has(key)) {
                    collector.fixedCosts.set(key, {
                        description: item.fixedCosts[i].description,
                        unit: item.fixedCosts[i].unit,
                        rate: item.fixedCosts[i].rate,
                        quantity: 0
                    })
                }
                collector.fixedCosts.get(key).quantity += item.fixedCosts[i].quantity;
            }
        }
    }

    static updateItemData(item, materialData, labourData, fixedCostData) {
        if (item.bundle) {
            for (let i = 0; i < item.bundle.length; i++) {
                Estimate.updateItemData(item.bundle[i], materialData, labourData, fixedCostData)
            }
        } else {
            if (materialData) {
                for (let i = 0; i < item.materials.length; i++) {
                    if (
                        (item.materials[i].name === materialData.from.name) &&
                        (item.materials[i].supplier === materialData.from.supplier) &&
                        (item.materials[i].cost === materialData.from.cost)
                    ) {
                        Object.assign(item.materials[i], materialData.to)
                    }
                }
            }
            if (labourData) {
                for (let i = 0; i < item.labour.length; i++) {
                    if (
                        (item.labour[i].name === labourData.from.name) &&
                        (item.labour[i].rate === labourData.from.rate)
                    ) {
                        Object.assign(item.labour[i], labourData.to)
                    }
                }
            }
            if (fixedCostData) {
                for (let i = 0; i < item.fixedCosts.length; i++) {
                    if (
                        (item.fixedCosts[i].description === fixedCostData.from.description) &&
                        (item.fixedCosts[i].unit === fixedCostData.from.unit) &&
                        (item.fixedCosts[i].rate === fixedCostData.from.rate)
                    ) {
                        Object.assign(item.fixedCosts[i], fixedCostData.to)
                    }
                }
            }
        }
    }

    static newCollector() {
        return {
            materials: new Map(),
            labour: new Map(),
            fixedCosts: new Map(),
            items: new Map(),
        }
    }

    static async find(id) {
        const data = await Http.get(route('api.v2.estimation.estimate', id))
        const obj = this.make(data);
        obj.recalculateEstimate();
        return obj;
    }

    static async create(packageReference, id) {
        const data = await Http.post(route('api.v2.estimation.estimate.create', packageReference), {uuid: id});
        return Model.make(data)
    }

    async duplicateAsRevision() {
        let id = uuid();
        await Estimate.create(this.packageReference, id)
        let estimate = Estimate.make(await Http.get(route('api.v2.estimation.estimate', id)));
        Object.assign(estimate, this.except(['id', 'externalInvites', 'saving', 'saveAgain', 'updateUUID']))
        await estimate.save()
        return await Estimate.find(id)
    }

    async reload() {
        let estimate = await Estimate.find(this.id)
        Object.assign(this, estimate)
    }

    setAttributes(attributes) {
        super.setAttributes(attributes)
        this.buildTokens()
        if (this.externalInvites) {
            this.externalInvites.forEach((i) => {
                i.invitedAt = moment(i.createdOn).format('DD/MM/YYYY HH:mm:ss');
            })
            this.externalInvites.sort((a, b) => moment(b.createdOn).valueOf() - moment(a.createdOn).valueOf())
        }
    }

    buildTokens() {
        let tokens = [
            this.reference,
            this.coverSubject ? this.coverSubject : '',
            this.title,
        ]
        this.tokens = tokens.join('|').toLowerCase();
    }

    getFormAttributes() {
        return this.except(['activityLog', 'id', 'saving', 'saveAgain', 'localStateStorage', 'error', 'colorMap', 'shapeMap', ' collectedItems', 'collectionBuildUUID', 'tokens', 'watchTrigger']);
    }

    async update(attributes = {}) {
        this.updateCounter = this.updateCounter ? this.updateCounter++ : 1;
        this.updateAssemblyMap()
        this.debouncedSave(attributes)
    }

    async save(attributes = {}) {
        this.updateReferences()
        const url = route('api.v2.estimation.estimate.store', this.id)
        const payload = this.constructor.parseAttributes({...this.getFormAttributes(), ...attributes})
        this.saving = true;
        eventBus.$emit('estimate-saving', this);
        await Http.put(url, payload).then(() => {
            eventBus.$emit('estimate-saved', this);
            this.loggedOut = false;
            this.saving = false;
            this.error = false;
        })
    }

    updateAssemblyMap() {
        this.assemblyMap = []
        for (let s = 0; s < this.scopeOfWorks.length; s++) {
            for (let t = 0; t < this.scopeOfWorks[s].tasks.length; t++) {
                for (let i = 0; i < this.scopeOfWorks[s].tasks[t].items.length; i++) {
                    let item = this.scopeOfWorks[s].tasks[t].items[i];
                    if (item.bundle) {
                        for (let b = 0; b < this.scopeOfWorks[s].tasks[t].items[i].bundle.length; b++) {
                            let bundleItem = this.scopeOfWorks[s].tasks[t].items[i].bundle[b]
                            let key = Estimate.itemAsKey(bundleItem)
                            let option = this.assemblyMap.find((i) => i.key === key);
                            if (option) {
                                option.quantity++;
                            } else {
                                this.assemblyMap.push({
                                    key: key,
                                    color: this.getColor(bundleItem),
                                    shape: this.getShape(bundleItem),
                                    title: this.getTitle(bundleItem),
                                    quantity: 1,
                                });
                            }
                        }
                    } else {
                        let key = Estimate.itemAsKey(item)
                        let option = this.assemblyMap.find((i) => i.key === key);
                        if (option) {
                            option.quantity++;
                        } else {
                            this.assemblyMap.push({
                                key: key,
                                color: this.getColor(item),
                                shape: this.getShape(item),
                                title: this.getTitle(item),
                                quantity: 1,
                            });
                        }
                    }
                }
            }
        }
        this.assemblyMap = this.assemblyMap.filter((i) => i.quantity > 1);
        this.colorMap = Object.fromEntries(Object.entries(this.colorMap).filter(([k, v]) => this.assemblyMap.some((i) => i.key === k)));
        this.shapeMap = Object.fromEntries(Object.entries(this.shapeMap).filter(([k, v]) => this.assemblyMap.some((i) => i.key === k)));
        this.titleMap = Object.fromEntries(Object.entries(this.titleMap).filter(([k, v]) => this.assemblyMap.some((i) => i.key === k)));
    }

    async delete() {
        await Http.delete(route('api.v2.estimation.estimate.delete', this.id))
    }


    /**
     * TODO : DELETE THIS! - We dont want ANYthing from classic in the new DDD modeling
     * @returns {Promise<void>}
     */
    async copyToClassicQuote() {
        const {data} = await Http.put(route('api.v1.estimates-v2.to-quote', this.id))
        return data
    }

    static make(attributes = {}) {
        const updateItem = (item, obj) => {
            item.quantity = (parseFloat(item.quantity) === 0) ? '' : item.quantity;
            for (let mi = 0; mi < item.materials.length; mi++) {
                item.materials[mi].cost = item.materials[mi].cost ? math.round(item.materials[mi].cost, 2) : 0
                item.materials[mi].waste = item.materials[mi].waste ? math.round(item.materials[mi].waste, 2) : 0
                item.materials[mi].total = item.materials[mi].total ? math.round(item.materials[mi].total, 2) : 0
            }
            for (let li = 0; li < item.labour.length; li++) {
                item.labour[li].total = item.labour[li].total ? math.round(item.labour[li].total, 2) : 0
            }
            for (let mmi = 0; mmi < item.fixedCosts.length; mmi++) {
                item.fixedCosts[mmi].quantity = item.fixedCosts[mmi].quantity ? math.round(item.fixedCosts[mmi].quantity, 2) : 0
                item.fixedCosts[mmi].total = item.fixedCosts[mmi].total ? math.round(item.fixedCosts[mmi].total, 2) : 0
            }
            item.materialBuildTotal = math.round(item.materials.reduce((a, material) => a + material.total, 0), 2)
            item.labourBuildTotal = math.round(item.labour.reduce((a, labour) => a + labour.total, 0), 2)
            item.fixedCostBuildTotal = math.round(item.fixedCosts.reduce((a, fixedCost) => a + fixedCost.total, 0), 2)
            // Totals
            item.buildTotal = math.round((item.materialBuildTotal + item.labourBuildTotal + item.fixedCostBuildTotal), 2)

            if (item.buildTotal) {
                item.buildTotalMarkedUp += math.round((item.buildTotal * (1 + (item.profit / 100))), 2)
                item.markupFixedValue = null
            } else {
                item.buildTotalMarkedUp = item.total + item.discount;
                item.markupFixedValue = item.buildTotalMarkedUp;
            }
            item.overheadsPercent = math.round((obj.overheads), 2)
            item.overheads = math.round((item.buildTotal * (obj.overheads / 100)), 2)

            item.fixedProfit = obj.profit !== item.profitPercent;

            item.total = item.quantity ? math.round(item.total, 2) : 0
            item.rate = item.quantity ? math.round((item.total / item.quantity), 2) : 0
            if (item.discount) {
                item.discountPercent = (item.discount / (item.total + item.discount)) * 100
            } else {
                item.discountPercent = 0
            }
            item.discount = math.round(item.discount, 2)
            item.adjustment = (item.total - item.discount) * (obj.adjustment / 100)
            item.mcd = (item.total - item.discount - item.adjustment) * (obj.mcd / 100)
        }

        let obj = super.make(attributes);
        if (Array.isArray(obj)) return obj;
        obj.defaultMarkup = parseFloat(obj.overheads) + parseFloat(obj.profit);
        for (let si = 0; si < obj.scopeOfWorks.length; si++) {
            obj.scopeOfWorks[si].defaultTitle = 'Untitled Section ' + (si + 1)
            obj.scopeOfWorks[si].total = 0
            for (let ti = 0; ti < obj.scopeOfWorks[si].tasks.length; ti++) {
                obj.scopeOfWorks[si].tasks[ti].defaultReference = (si + 1) + '.' + (ti + 1)
                obj.scopeOfWorks[si].tasks[ti].total = 0
                for (let ii = 0; ii < obj.scopeOfWorks[si].tasks[ti].items.length; ii++) {
                    obj.scopeOfWorks[si].tasks[ti].items[ii].defaultReference = (si + 1) + '.' + (ti + 1) + '.' + (ii + 1)
                    if (isNull(obj.scopeOfWorks[si].tasks[ti].items[ii].bundle)) {
                        updateItem(obj.scopeOfWorks[si].tasks[ti].items[ii], obj);
                    } else {
                        obj.scopeOfWorks[si].tasks[ti].items[ii].total = 0
                        obj.scopeOfWorks[si].tasks[ti].items[ii].buildTotal = 0
                        obj.scopeOfWorks[si].tasks[ti].items[ii].buildTotalMarkedUp = 0
                        obj.scopeOfWorks[si].tasks[ti].items[ii].discount = 0
                        obj.scopeOfWorks[si].tasks[ti].items[ii].materialBuildTotal = 0
                        obj.scopeOfWorks[si].tasks[ti].items[ii].labourBuildTotal = 0
                        obj.scopeOfWorks[si].tasks[ti].items[ii].fixedCostBuildTotal = 0
                        for (let b = 0; b < obj.scopeOfWorks[si].tasks[ti].items[ii].bundle.length; b++) {
                            updateItem(obj.scopeOfWorks[si].tasks[ti].items[ii].bundle[b], obj)
                            obj.scopeOfWorks[si].tasks[ti].items[ii].total += obj.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].total
                            obj.scopeOfWorks[si].tasks[ti].items[ii].buildTotal += obj.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].buildTotal
                            obj.scopeOfWorks[si].tasks[ti].items[ii].buildTotalMarkedUp += obj.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].buildTotalMarkedUp
                            obj.scopeOfWorks[si].tasks[ti].items[ii].discount += obj.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].discount
                            obj.scopeOfWorks[si].tasks[ti].items[ii].materialBuildTotal += obj.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].materialBuildTotal
                            obj.scopeOfWorks[si].tasks[ti].items[ii].labourBuildTotal += obj.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].labourBuildTotal
                            obj.scopeOfWorks[si].tasks[ti].items[ii].fixedCostBuildTotal += obj.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].fixedCostBuildTotal
                            obj.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].reference = obj.scopeOfWorks[si].tasks[ti].items[ii].reference
                            obj.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].defaultReference = obj.scopeOfWorks[si].tasks[ti].items[ii].defaultReference
                        }
                        obj.scopeOfWorks[si].tasks[ti].items[ii].markupFixedValue = false;
                    }
                    obj.scopeOfWorks[si].tasks[ti].total += obj.scopeOfWorks[si].tasks[ti].items[ii].total
                }
                obj.scopeOfWorks[si].total += obj.scopeOfWorks[si].tasks[ti].total
            }
        }
        obj.buildCollectedItems();
        obj.debouncedSave = debounce((a) => obj.save(a), 700, {leading: false});
        obj.updateReferences = debounce(() => obj.recalculateEstimate(), 200, {leading: true, maxWait: 2000});
        if (!obj.colorMap) {
            obj.colorMap = {}
        }
        if (!obj.shapeMap) {
            obj.shapeMap = {}
        }
        if (!obj.assemblyMap) {
            obj.assemblyMap = []
        }
        return obj;
    }

    setItemTotal(item, value) {
        item.total = value
        item.rate = math.round((item.quantity ? (item.total ? item.total : 0) / item.quantity : 0), 2)
        this.updateReferences();
    }

    setItemQuantity(item, value) {
        if (item.quantity && value) {
            if (item.markupFixedValue) {
                item.markupFixedValue = math.round((item.markupFixedValue / item.quantity) * value, 2)
            }
        }
        item.quantity = value;
        this.updateReferences(); // Build it up, to find correct totals
    }

    recalculateEstimate() {
        const updateItem = (item) => {
            // Calculate Materials
            let buildMaterialTotal = 0;
            for (let mi = 0; mi < item.materials.length; mi++) {
                item.materials[mi].waste = Math.min(99.99, item.materials[mi].waste ? math.round(item.materials[mi].waste, 2) : 0)
                if (item.quantity) {
                    item.materials[mi].quantity = item.materials[mi].coverage ? ((item.quantity / (item.materials[mi].coverage * (1 - (item.materials[mi].waste / 100))))) : 0
                    item.materials[mi].coverage = item.materials[mi].quantity ? item.materials[mi].coverage : 0
                    item.materials[mi].total = item.materials[mi].cost ? math.round((item.materials[mi].cost * item.materials[mi].quantity), 2) : 0
                } else {
                    item.materials[mi].total = 0;
                }
                buildMaterialTotal += item.materials[mi].total;
            }
            item.materialBuildTotal = math.round(buildMaterialTotal, 2);
            // Calculate Labours
            let buildLabourTotal = 0
            for (let li = 0; li < item.labour.length; li++) {
                item.labour[li].total = item.labour[li].rate ? math.round(item.labour[li].rate * item.quantity, 2) : 0
                buildLabourTotal += item.labour[li].total
            }
            item.labourBuildTotal = math.round(buildLabourTotal, 2)
            // Calculate Fixed Costs
            let buildFixedCostTotal = 0
            for (let fi = 0; fi < item.fixedCosts.length; fi++) {
                item.fixedCosts[fi].rate = math.round((item.fixedCosts[fi].rate ? item.fixedCosts[fi].rate : 0), 2)
                item.fixedCosts[fi].quantity = math.round((item.fixedCosts[fi].quantity ? item.fixedCosts[fi].quantity : 0) * 1000) / 1000
                item.fixedCosts[fi].total = math.round((item.fixedCosts[fi].quantity * item.fixedCosts[fi].rate), 2)
                buildFixedCostTotal += item.fixedCosts[fi].total
            }
            item.fixedCostBuildTotal = math.round(buildFixedCostTotal, 2)

            // Calculate Build Totals
            item.rawBuildTotal = item.materialBuildTotal + item.labourBuildTotal + item.fixedCostBuildTotal;
            item.buildTotal = math.round(item.rawBuildTotal, 2)
            if (!item.buildTotal) {
                item.buildTotalMarkedUp = item.markupFixedValue ? item.markupFixedValue : 0
            } else {
                item.buildTotalMarkedUp = 0;
                item.markupFixedValue = null;
            }
            //calculate the Markup (OHP)
            item.markup = this.overheads + item.profitPercent;

            let calc = () => {
                if ((!item.fixedProfit) || (item.markupFixedValue)) {
                    item.profitPercent = this.profit;
                }
                if (item.markupFixedValue) {
                    let total = item.buildTotalMarkedUp / (1 + (100 / (this.overheads + item.profitPercent)))
                    let both = this.overheads + item.profitPercent;

                    item.rawOverheads = (both ? (total / (both) * this.overheads) : 0)
                    item.rawProfit = (both ? (total / (both) * item.profitPercent) : 0)
                    item.overheads = math.round(item.rawOverheads, 2)
                    item.profit = math.round(item.rawProfit, 2)
                } else {
                    item.rawOverheads = item.buildTotal * (this.overheads / 100)
                    item.rawProfit = item.buildTotal * (item.profitPercent / 100)
                    item.overheads = math.round(item.rawOverheads, 2)
                    item.profit = math.round(item.rawProfit, 2)
                    item.buildTotalMarkedUp = math.round((item.rawBuildTotal + item.rawOverheads + item.rawProfit), 2)
                }
                item.valueWithDefaultOHP = math.round(item.rawBuildTotal * (1 + (this.profit + this.overheads) / 100), 2)
            }
            calc();

            if (item.fixedProfit && (item.buildTotalMarkedUp === item.valueWithDefaultOHP)) {
                item.fixedProfit = false
                calc();
            }

            if (item.discountPercent) {
                item.discount = math.round(item.discountPercent / 100 * item.buildTotalMarkedUp, 2)
            } else {
                item.discount = 0
                item.discountPercent = 0
            }
            item.total = math.round(item.buildTotalMarkedUp - item.discount, 2)
            item.rate = item.quantity ? math.round(item.total / item.quantity, 2) : 0

            item.adjustment = math.round(item.total * (this.adjustment / 100), 2)
            item.mcd = math.round((item.total - item.adjustment) * (this.mcd / 100), 2)
            item.finalTotal = math.round((item.buildTotalMarkedUp - item.discount - item.adjustment - item.mcd), 2)

            item.effectiveOHP = math.round((item.markupFixedValue ? (item.rawProfit + item.rawOverheads - item.discount - item.adjustment - item.mcd) : item.finalTotal - item.buildTotal), 2)
            item.runningBuildTotal = (item.buildTotal ? item.buildTotal : item.markupFixedValue - (item.rawProfit + item.rawOverheads))
            item.effectiveOHPPercent = item.effectiveOHP / item.runningBuildTotal * 100
            if (isNaN(item.effectiveOHPPercent)) {
                item.effectiveOHPPercent = item.profitPercent + item.overheadsPercent;
            }
        }
        for (let pi = 0; pi < this.preliminaries.length; pi++) {
            this.preliminaries[pi].total = 0
            for (let i = 0; i < this.preliminaries[pi].items.length; i++) {
                this.preliminaries[pi].total += this.preliminaries[pi].items[i].total
                this.preliminaries[pi].rate += math.round(this.preliminaries[pi].items[i].total / this.preliminaries[pi].items[i].quantity, 2)
            }
        }
        for (let si = 0; si < this.scopeOfWorks.length; si++) {
            if (this.scopeOfWorks[si].defaultTitle === this.scopeOfWorks[si].title) {
                this.scopeOfWorks[si].title = 'Untitled Section ' + (si + 1);
            }
            this.scopeOfWorks[si].defaultTitle = 'Untitled Section ' + (si + 1)
            for (let ti = 0; ti < this.scopeOfWorks[si].tasks.length; ti++) {
                if (this.scopeOfWorks[si].tasks[ti].defaultReference === this.scopeOfWorks[si].tasks[ti].reference) {
                    this.scopeOfWorks[si].tasks[ti].reference = (si + 1) + '.' + (ti + 1)
                }
                this.scopeOfWorks[si].tasks[ti].defaultReference = (si + 1) + '.' + (ti + 1)

                // Flatten any bundles inside bundles
                for (let ii = 0; ii < this.scopeOfWorks[si].tasks[ti].items.length; ii++) {
                    if (this.scopeOfWorks[si].tasks[ti].items[ii].bundle && !isNull(this.scopeOfWorks[si].tasks[ti].items[ii].bundle)) {
                        // flatten any if required
                        for (let i = 0; i < this.scopeOfWorks[si].tasks[ti].items[ii].bundle.length; i++) {
                            if (this.scopeOfWorks[si].tasks[ti].items[ii].bundle[i].bundle && !isNull(this.scopeOfWorks[si].tasks[ti].items[ii].bundle[i].bundle)) {
                                this.scopeOfWorks[si].tasks[ti].items[ii].bundle.splice(i, 1, ...this.scopeOfWorks[si].tasks[ti].items[ii].bundle[i].bundle)
                            }
                        }
                    }
                }

                // remove any blank bundles
                for (let ii = 0; ii < this.scopeOfWorks[si].tasks[ti].items.length; ii++) {
                    if (!this.scopeOfWorks[si].tasks[ti].items[ii].bundle || !this.scopeOfWorks[si].tasks[ti].items[ii].bundle.length) {
                        this.scopeOfWorks[si].tasks[ti].items[ii].bundle = null;
                    }
                }

                for (let ii = 0; ii < this.scopeOfWorks[si].tasks[ti].items.length; ii++) {
                    if (this.scopeOfWorks[si].tasks[ti].items[ii].defaultReference === this.scopeOfWorks[si].tasks[ti].items[ii].reference) {
                        this.scopeOfWorks[si].tasks[ti].items[ii].reference = (si + 1) + '.' + (ti + 1) + '.' + (ii + 1)
                    }
                    this.scopeOfWorks[si].tasks[ti].items[ii].defaultReference = (si + 1) + '.' + (ti + 1) + '.' + (ii + 1)
                    if (isNull(this.scopeOfWorks[si].tasks[ti].items[ii].bundle)) {
                        updateItem(this.scopeOfWorks[si].tasks[ti].items[ii])
                    } else {
                        this.scopeOfWorks[si].tasks[ti].items[ii].total = 0
                        this.scopeOfWorks[si].tasks[ti].items[ii].buildTotal = 0
                        this.scopeOfWorks[si].tasks[ti].items[ii].buildTotalMarkedUp = 0
                        this.scopeOfWorks[si].tasks[ti].items[ii].discount = 0
                        this.scopeOfWorks[si].tasks[ti].items[ii].materialBuildTotal = 0
                        this.scopeOfWorks[si].tasks[ti].items[ii].labourBuildTotal = 0
                        this.scopeOfWorks[si].tasks[ti].items[ii].fixedCostBuildTotal = 0
                        this.scopeOfWorks[si].tasks[ti].items[ii].overheads = 0
                        this.scopeOfWorks[si].tasks[ti].items[ii].profit = 0
                        this.scopeOfWorks[si].tasks[ti].items[ii].rawOverheads = 0
                        this.scopeOfWorks[si].tasks[ti].items[ii].rawProfit = 0
                        this.scopeOfWorks[si].tasks[ti].items[ii].mcd = 0
                        this.scopeOfWorks[si].tasks[ti].items[ii].adjustment = 0
                        this.scopeOfWorks[si].tasks[ti].items[ii].finalTotal = 0
                        this.scopeOfWorks[si].tasks[ti].items[ii].effectiveOHP = 0
                        this.scopeOfWorks[si].tasks[ti].items[ii].markupFixedValue = false
                        this.scopeOfWorks[si].tasks[ti].items[ii].runningBuildTotal = 0

                        for (let b = 0; b < this.scopeOfWorks[si].tasks[ti].items[ii].bundle.length; b++) {
                            updateItem(this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b])
                            this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].color = this.scopeOfWorks[si].tasks[ti].items[ii].color;
                            this.scopeOfWorks[si].tasks[ti].items[ii].total += this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].total
                            this.scopeOfWorks[si].tasks[ti].items[ii].buildTotal += this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].buildTotal
                            this.scopeOfWorks[si].tasks[ti].items[ii].buildTotalMarkedUp += this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].buildTotalMarkedUp
                            this.scopeOfWorks[si].tasks[ti].items[ii].discount += this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].discount
                            this.scopeOfWorks[si].tasks[ti].items[ii].materialBuildTotal += this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].materialBuildTotal
                            this.scopeOfWorks[si].tasks[ti].items[ii].labourBuildTotal += this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].labourBuildTotal
                            this.scopeOfWorks[si].tasks[ti].items[ii].fixedCostBuildTotal += this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].fixedCostBuildTotal
                            this.scopeOfWorks[si].tasks[ti].items[ii].overheads += this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].overheads
                            this.scopeOfWorks[si].tasks[ti].items[ii].profit += this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].profit
                            this.scopeOfWorks[si].tasks[ti].items[ii].rawOverheads += this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].rawOverheads
                            this.scopeOfWorks[si].tasks[ti].items[ii].rawProfit += this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].rawProfit
                            this.scopeOfWorks[si].tasks[ti].items[ii].mcd += this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].mcd
                            this.scopeOfWorks[si].tasks[ti].items[ii].adjustment += this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].adjustment
                            this.scopeOfWorks[si].tasks[ti].items[ii].finalTotal += this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].finalTotal
                            this.scopeOfWorks[si].tasks[ti].items[ii].effectiveOHP += this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].effectiveOHP
                            this.scopeOfWorks[si].tasks[ti].items[ii].markupFixedValue = this.scopeOfWorks[si].tasks[ti].items[ii].markupFixedValue || this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].markupFixedValue
                            this.scopeOfWorks[si].tasks[ti].items[ii].runningBuildTotal += this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].runningBuildTotal;
                            this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].defaultReference = this.scopeOfWorks[si].tasks[ti].items[ii].defaultReference
                            this.scopeOfWorks[si].tasks[ti].items[ii].bundle[b].reference = this.scopeOfWorks[si].tasks[ti].items[ii].reference
                        }
                        this.scopeOfWorks[si].tasks[ti].items[ii].rate = math.round(this.scopeOfWorks[si].tasks[ti].items[ii].quantity ? (this.scopeOfWorks[si].tasks[ti].items[ii].buildTotalMarkedUp / this.scopeOfWorks[si].tasks[ti].items[ii].quantity) : 0, 2);
                        this.scopeOfWorks[si].tasks[ti].items[ii].profitPercent = this.scopeOfWorks[si].tasks[ti].items[ii].rawProfit / this.scopeOfWorks[si].tasks[ti].items[ii].runningBuildTotal * 100
                        this.scopeOfWorks[si].tasks[ti].items[ii].effectiveOHPPercent = (this.scopeOfWorks[si].tasks[ti].items[ii].effectiveOHP / this.scopeOfWorks[si].tasks[ti].items[ii].runningBuildTotal) * 100
                    }
                }
            }
        }
        this.total = this.scopeOfWorks.filter((i) => !i.optional).reduce((a, section) => a + section.tasks.reduce(
            (a, task) => a + (task.items.reduce(
                    (a, item) => a + (item.finalTotal ? item.finalTotal : 0), 0)
            ), 0
        ), 0) + this.preliminaries.reduce((a, task) => a + (task.items.reduce(
                (a, item) => a + (item.total ? item.total : 0), 0)
        ), 0)
    }

    updateOverheads(val) {
        // if ANY item has negative profit, make sure there
        // is room for the new overheads - if not, adjust the profit to compensate
        for (let s = 0; s < this.scopeOfWorks.length; s++) {
            for (let t = 0; t < this.scopeOfWorks[s].tasks.length; t++) {
                for (let i = 0; i < this.scopeOfWorks[s].tasks[t].items.length; i++) {
                    if (this.scopeOfWorks[s].tasks[t].items[i].profitPercent + val < -100) {
                        this.scopeOfWorks[s].tasks[t].items[i].profitPercent = -100 + val;
                    }
                }
            }
        }
        this.overheads = val;
    }

    disableSnapshots = false

    async send(to, message) {
        const url = route('api.v2.estimation.estimate.send', [this.id])
        let success
        try {
            success = await Http.post(url, {to, message})
        } catch (error) {
            return error.response
        }
        const data = await Http.get(route('api.v2.estimation.estimate', [this.id]))
        return success;
    }

    async revoke(sendRequestId) {
        const url = route('api.v2.estimation.estimate.revoke', [this.id, sendRequestId])
        let success
        try {
            success = await Http.post(url, {})
        } catch (error) {
            return error.response
        }
        const data = await Http.get(route('api.v2.estimation.estimate', [this.id]))
        Object.assign(this, data)
        return success;
    }

    async resend(sendRequestId) {
        const url = route('api.v2.estimation.estimate.resend', [this.id, sendRequestId])
        let success
        try {
            success = await Http.post(url, {})
        } catch (error) {
            return error.response
        }
        const data = await Http.get(route('api.v2.estimation.estimate', [this.id]))
        Object.assign(this, data)
        return success;
    }

    buildCollectedItems() {
        delete this.collectedItems;
        this.collectedItems = Estimate.newCollector();
        for (let s = 0; s < this.scopeOfWorks.length; s++) {
            for (let t = 0; t < this.scopeOfWorks[s].tasks.length; t++) {
                for (let i = 0; i < this.scopeOfWorks[s].tasks[t].items.length; i++) {
                    let item = this.scopeOfWorks[s].tasks[t].items[i];
                    Estimate.buildMapFromItem(item, this.collectedItems)
                }
            }
        }
        this.collectionBuildUUID = uuid();
    }

    addSnapshot(dontRebuildCollection = false) {
        if (!dontRebuildCollection) {
            this.buildCollectedItems()
        }
        if (this.disableSnapshots) return
        let store = this.getSnapshotStore()
        let data = JSON.stringify(this.except(['saving', 'saveAgain', 'updateUUID', 'collectionBuildUUID', 'collectedItems', 'localStateStorage']))
        if ((!store.states.length) || (store.states[store.states.length - 1] !== data)) {
            store.states.push(data);
            this.putSnapshotStore(store)
            eventBus.$emit('estimate-history-state-updated', {estimate: this, states: store.states})
        }
    }

    clearSnapshots(fromPosition, count = null) {
        const store = this.getSnapshotStore()
        store.states.splice(fromPosition, isNull(count) ? store.states.length - fromPosition : count)
        this.putSnapshotStore(store);
    }

    getSnapshotStore() {
        if (!this.localStateStorage) {
            this.localStateStorage = {}
        }
        if (!this.localStateStorage['estimates-history:' + this.id]) {
            this.localStateStorage['estimates-history:' + this.id] = {states: []}
        }
        return this.localStateStorage['estimates-history:' + this.id]
    }

    putSnapshotStore(store) {
        this.localStateStorage['estimates-history:' + this.id] = store;
        // TODO - remove remote storage for now
        User.clearStorageKey('estimates-history:' + this.id);
    }

    getSummaryParagraph(title) {
        const m = this.summaryParagraphs.find((p) => p.title.toLowerCase() === title.toLowerCase())
        return m ? m.content : false
    }
}
